import { forwardRef, useLayoutEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { useCombineRefs } from '../../../../hooks/use-combine-refs';
import { useElementSize } from '../../../../hooks/use-element-size';
import { usePrevious } from '../../../../hooks/use-previous';
import { Button, ButtonProps } from '../Button';
import { Spinner } from '../Spinner';

export type FormSubmitButtonProps = Omit<ButtonProps, 'type'> & {
  saveWidth?: boolean;
};

export const FormSubmitButton = forwardRef<HTMLButtonElement, FormSubmitButtonProps>((props, propsRef) => {
  const { className, style, children, saveWidth = true, iconLeft, ...buttonProps } = props;

  const [buttonWidth, setButtonWidth] = useState<number | string>('');

  const {
    formState: { isSubmitting },
  } = useFormContext();

  const {
    ref,
    size: { width: calculatedButtonWidth },
  } = useElementSize();

  const combinedRef = useCombineRefs([ref, propsRef]);

  const prevIsSubmitting = usePrevious(isSubmitting);

  useLayoutEffect(() => {
    if (isSubmitting && !prevIsSubmitting) {
      setButtonWidth(calculatedButtonWidth);
    } else if (!isSubmitting && prevIsSubmitting) {
      setButtonWidth('');
    }
  }, [calculatedButtonWidth, isSubmitting, prevIsSubmitting]);

  return (
    <Button
      {...buttonProps}
      className={className}
      ref={combinedRef}
      type="submit"
      disabled={buttonProps.disabled || isSubmitting}
      iconLeft={isSubmitting ? null : iconLeft}
      style={
        saveWidth && buttonWidth
          ? {
              ...style,
              width: isSubmitting ? buttonWidth : undefined,
            }
          : style
      }
    >
      {isSubmitting ? <Spinner /> : children}
    </Button>
  );
});

FormSubmitButton.displayName = 'FormSubmitButton';
